/**
 * مخزن صغير للصور المفكوكة الخاصة براستر الطقس.
 *
 * إحماء المتصفح التقليدي يضمن التنزيل فقط، لكن إنشاء Image جديد قد يفرض فك PNG ثانيةً
 * قبل رفعه إلى WebGL. الاحتفاظ بعدد محدود من الصور المفكوكة الجاهزة يجعل الإطار التالي
 * جاهزاً للرفع فوراً من دون إنقاص دقة النسيج أو إعادة استخدام صورة قديمة.
 *
 * **الفكّ خارج الخيط الرئيسي (ImageBitmap).**
 * كان المسار: `new Image()` ثم `img.decode()`. رغم أن `decode()` يَعِد بفكّ غير حاجب،
 * تبقى نتيجته صورةً يفكّها المتصفح في سياق العرض، ويظلّ `texImage2D` عليها يكلّف نسخاً
 * ومعالجة على الخيط الرئيسي — 1440×721 = ~4 ميغابايت لكل رفع، أي نتوء 8–20 مللي عند كل
 * انتقال ساعة. وهذا نتوء **مرئي**: إطار مفقود بالضبط في اللحظة التي يُفترض أن يكون فيها
 * الانتقال أنعم ما يكون.
 *
 * `createImageBitmap` يفكّ الصورة في خيط المتصفح الداخلي ويُسلّم كائناً جاهزاً للرفع إلى
 * GPU بلا تحويل إضافي، فيسقط النتوء إلى ما دون المللي. النتيجة بكسل-ببكسل مطابقة —
 * لا ضغط ولا تصغير ولا أي مساس بالدقّة.
 *
 * الاسم `loadRasterImage` باقٍ كما هو (كل المستدعين يستعملون `.width/.height` فقط)،
 * والنوع اتحاد يشمل `HTMLImageElement` لأجل المتصفحات النادرة بلا `createImageBitmap`.
 */

/**
 * صورة راستر مفكوكة جاهزة للرفع إلى WebGL أو للرسم على canvas.
 *
 * `.width/.height` متاحان على الطرفين ويساويان الأبعاد الأصلية: على `ImageBitmap` بداهةً،
 * وعلى `HTMLImageElement` غير المُلحق بالمستند وبلا CSS يساويان `naturalWidth/Height`.
 * لذلك **يجب** استعمال `.width/.height` لا `.naturalWidth` عند القراءة من هذا الكاش.
 */
export type RasterImage = ImageBitmap | HTMLImageElement;

const images = new Map<string, RasterImage>();
const pending = new Map<string, Promise<RasterImage>>();

const canBitmap = typeof createImageBitmap === 'function';

/**
 * سعة الكاش.
 *
 * الكلفة ذاكرة: كل صورة 1440×721 تشغل ~4 ميغابايت مفكوكة، فـ40 ≈ 160 ميغابايت في
 * أسوأ حال. لذلك تبقى الأجهزة المحدودة (‏4 غيغابايت أو أقلّ) على سعة أصغر.
 *
 * أثناء تحريك الشريط الزمني تحتاج الطبقة الإطار الحالي والتالي **لكل نوع**، ومعهما ما
 * أُحمي أمامها؛ فكاش أصغر من نافذة الإحماء يعني طرداً ثم إعادة فكّ عند كل خطوة = تلعثم
 * بدل حركة متّصلة. وسحب الشريط سريعاً للخلف والأمام بين ساعات زارها المستخدم للتوّ كان
 * يُعيد الفكّ رغم توفّر البايتات في كاش الشبكة.
 */
function maxImages(): number {
    const memory = typeof navigator === 'undefined'
        ? undefined
        : (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
    return memory && memory <= 4 ? 18 : 40;
}

function touch(src: string, image: RasterImage): RasterImage {
    images.delete(src);
    images.set(src, image);
    while (images.size > maxImages()) {
        const oldest = images.keys().next().value;
        if (!oldest) break;
        images.delete(oldest);
        /*
         * **لا نستدعي close() هنا** — وهذا مقصود، وقد كلّف تشخيصاً.
         *
         * الإغراء واضح: ImageBitmap يحجز ذاكرة خارج كومة الـ JS، وclose() يحرّرها فوراً.
         * لكن الطرد من هذا الكاش **لا يعني** أن أحداً لم يعد يمسك الصورة: الطبقة تحتفظ
         * بمرجع إلى الإطارين الحالي والتالي، وقارئ التلميح بمرجع آخر، والتعبئة الخلفية
         * (‏121 إطاراً) تُدوّر الكاش بسرعة فتطرد صوراً قيد الاستعمال في نفس اللحظة.
         * وbitmap مُغلق يرمي عند drawImage/texImage2D، فيبتلع الاستثناءَ حارسُ try/catch
         * في الطبقة ولا يُرسم شيء — خريطة سوداء بلا خطأ في الطرفية.
         *
         * إسقاط المرجع كافٍ: جامع القمامة يحرّر الذاكرة الخارجية حين لا يبقى مالك، وهذا
         * بالضبط سلوك مخزن الصور السابق. حدّ السعة أعلاه هو ضابط الذاكرة، لا close().
         */
    }
    return image;
}

/** المسار القديم: عنصر Image — يبقى احتياطاً حين لا يتوفّر createImageBitmap. */
function loadViaImage(src: string): Promise<RasterImage> {
    return new Promise<RasterImage>((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.decoding = 'async';
        image.onload = () => {
            const decoded = typeof image.decode === 'function' ? image.decode().catch(() => undefined) : Promise.resolve();
            void decoded.then(() => resolve(image));
        };
        image.onerror = () => reject(new Error(`تعذّر تحميل نسيج الطقس: ${src}`));
        image.src = src;
    });
}

/**
 * المسار السريع: fetch ثم فكّ خارج الخيط الرئيسي.
 *
 * `cache: 'default'` مقصود — الروابط مرقّمة بـ `?v=run_epoch` فكاش المتصفح/الحافة صالح
 * ليوم كامل، وهذا ما يجعل إعادة زيارة ساعة سبق تحميلها مجانية على الشبكة.
 */
function loadViaBitmap(src: string): Promise<RasterImage> {
    return fetch(src, { mode: 'cors', credentials: 'omit', cache: 'default' })
        .then((r) => {
            if (!r.ok) {
                // 404/410 = الملفّ غير موجود على الخادم. نُسجّله كمفقود دائم ونرمي خطأً
                // **لا يُعاد معه المحاولة ولا يُسقَط إلى مسار الصورة** — راجع markMissing.
                if (r.status === 404 || r.status === 410) {
                    markMissing(src);
                    throw new MissingRasterError(src, r.status);
                }
                // غير ذلك (5xx، ‏522 من Cloudflare…) **عطلٌ عابر لا فقدان**. كان يُرمى
                // MissingRasterError لكل حالة غير ok، فتكفّ الطبقة عن الطلب نهائياً: يبقى
                // الإطار الأول فارغاً حتى يغيّر المستخدم الرابط بتحريك الأيام أو التشغيل —
                // «الخريطة بلا بيانات حتى أحرّكها» (رُصد مع انقطاعات 522 في 2026-09-13).
                // ولا نسقط هنا إلى مسار الصورة: هو الخادم نفسه، فطلبٌ ثانٍ فوراً لا يفيد.
                throw new TransientRasterError(src, r.status);
            }
            return r.blob();
        })
        .then((blob) => createImageBitmap(blob))
        // فشل الفكّ/الجلب (CORS على مسار مختلف، أو Blob تالف) لا يُسقط الطبقة: نسقط
        // إلى المسار القديم الذي يمرّ بمكدّس تحميل الصور العادي في المتصفح. أمّا المفقود
        // فلا معنى لإعادة طلبه بمكدّس آخر: النتيجة 404 نفسها.
        .catch((e) => {
            if (e instanceof MissingRasterError || e instanceof TransientRasterError) throw e;
            return loadViaImage(src);
        });
}

/**
 * سجلّ الملفّات المفقودة على الخادم (404) — لا يُعاد طلبها في هذه الجلسة.
 *
 * سببه مقيس من الإنتاج: ‏meta.json لنموذج ICON يعلن `hours` أكبر ممّا وُلِّد فعلاً (‏دالّة
 * `frame_count` في سكربت التوليد تأخذ **أعلى** فهرس عبر كل أنواع الطبقات، فطبقةٌ واحدة
 * أطول ترفع العدد المعلن لبقيّة الطبقات). فتطلب الواجهة ساعات لا وجود لها — رُصد
 * `wind_125/126/127` و`humidity_126` — وكل واحد منها كان يُطلب **ثلاث إلى أربع مرّات**:
 * محاولتا إعادة في `loadInto`، وسقوط إلى `loadViaImage`، ومناداة من الإحماء ومن الطبقة
 * معاً. أي عاصفة طلبات فاشلة تزاحم الإطارات الموجودة فعلاً وتُبطئ ما يحتاجه المستخدم.
 *
 * السجلّ في الذاكرة فقط: دورة توليد جديدة تُعيد تحميل الصفحة عادةً، ورقم `?v=run_epoch`
 * يتغيّر معها فتصير الروابط جديدة كلياً — فلا خطر من تثبيت «مفقود» على ملفّ وُلِّد لاحقاً.
 */
const missing = new Set<string>();

function markMissing(src: string): void {
    missing.add(src);
    if (missing.size > 3000) missing.clear();
}

/** خطأ ملفّ غير موجود — يميّزه المستدعي كي لا يُعيد المحاولة (راجع loadInto). */
export class MissingRasterError extends Error {
    constructor(src: string, public readonly status: number) {
        super(`نسيج غير موجود (${status}): ${src}`);
        this.name = 'MissingRasterError';
    }
}

/** ردّ خادم فاشل لكنه عابر (5xx/522) — يُعاد طلبه، بخلاف MissingRasterError. */
export class TransientRasterError extends Error {
    constructor(src: string, public readonly status: number) {
        super(`تعذّر جلب النسيج مؤقتاً (${status}): ${src}`);
        this.name = 'TransientRasterError';
    }
}

/** هل ثبت أن هذا الرابط غير موجود على الخادم؟ */
export function isRasterMissing(src: string): boolean {
    return missing.has(src);
}

/**
 * مخزن الصور المولَّدة محلياً (مفاتيح `mem:`) — **منفصل عن كاش الشبكة ولا يخضع لطرده**.
 *
 * لماذا منفصل: كاش الشبكة يطرد بـ LRU بسعة أربعين. نسيج تراكم الأمطار كان يُدرج فيه،
 * فيُطرد بعد قليل، ثم يطلبه العارض بمفتاحه فيسقط النداء إلى fetch على «رابط» اسمه
 * `mem:accum:…` — وهو ليس رابطاً، فيرمي المتصفّح «URL scheme mem is not supported»
 * ويختفي حقل الأمطار. المفاتيح الصناعية لا مصدر لها على الشبكة، فطردها يعني ضياعها
 * نهائياً؛ لذلك تسكن مخزناً خاصاً يملك مالكها (rasterSampler) حذفَ مدخلاته.
 */
const memImages = new Map<string, RasterImage>();

/** سعة مخزن الصور المولَّدة. تكفي نافذة التراكم القريبة (حالي/تالٍ + إحماء ثلاثة). */
const MAX_MEM_IMAGES = 12;

/** يعيد صورة مكتملة التنزيل والفك، مع توحيد الطلبات المتزامنة للرابط نفسه. */
export function loadRasterImage(src: string): Promise<RasterImage> {
    if (src.startsWith('mem:')) {
        const mem = memImages.get(src);
        // لا سقوط إلى الشبكة: لا وجود لهذا المفتاح خارج الذاكرة. الرفض هنا يجعل الطبقة
        // تُبقي آخر إطار جاهز معروضاً (displayedUrl) بدل رمي خطأ شبكة لا معنى له.
        return mem ? Promise.resolve(mem) : Promise.reject(new Error(`نسيج مولَّد غير متاح: ${src}`));
    }

    const cached = images.get(src);
    if (cached) return Promise.resolve(touch(src, cached));

    // ثبت فقدانه سابقاً — لا رحلة شبكة ثانية (راجع سجلّ missing).
    if (missing.has(src)) return Promise.reject(new MissingRasterError(src, 404));

    const inFlight = pending.get(src);
    if (inFlight) return inFlight;

    const task = (canBitmap ? loadViaBitmap(src) : loadViaImage(src))
        // الفكّ الناجح يعني بايتات كاملة في كاش المتصفّح — يُسجَّل هنا أيضاً كي لا يعتمد
        // امتلاء الشريط على المسار الخلفي وحده: إطارٌ عرضه المستخدم فعلاً هو أوثق دليل.
        .then((img) => { markFetched(src); return touch(src, img); })
        .finally(() => pending.delete(src));

    pending.set(src, task);
    return task;
}

/**
 * يُدرج صورة **مولَّدة محلياً** في الكاش تحت مفتاح صناعي، فتصير متاحة لكل من يطلبها
 * عبر `loadRasterImage` كأنها ملفّ عادي.
 *
 * الحاجة إليها جاءت من نسيج تراكم الأمطار: كان يُبنى كـ ImageData ثم **يُرمَّز PNG**
 * (`canvas.toBlob`) ثم يُنشَر كـ blob URL ثم **يُفكّ PNG من جديد** عند الرفع إلى GPU.
 * ترميز 1440×721 وفكّه رحلة ذهاب وإياب مكلفة على الخيط الرئيسي لكل إطار، وهي سبب كون
 * طبقة الأمطار أبطأ الطبقات في التنقّل بفارق واضح. البكسلات بين أيدينا أصلاً، فلا معنى
 * لتحويلها إلى PNG لنعيد قراءتها فوراً.
 *
 * المفاتيح الصناعية تبدأ بـ `mem:` كي لا تُشبه رابطاً حقيقياً أبداً.
 */
export function putRasterImage(key: string, image: RasterImage): void {
    memImages.delete(key);
    memImages.set(key, image);          // إعادة الإدراج = أحدث استعمالاً
    while (memImages.size > MAX_MEM_IMAGES) {
        const oldest = memImages.keys().next().value;
        if (oldest === undefined) break;
        memImages.delete(oldest);
    }
}

/** يُسقط صورة مولَّدة (عند تبديل النموذج مثلاً) — مالكها وحده يعرف متى بطلت. */
export function dropRasterImage(key: string): void {
    memImages.delete(key);
}

/** إحماء اختياري لا يرمي خطأ إلى واجهة المستخدم. يفكّ الصورة ويضعها في الكاش. */
export function prefetchRasterImage(src: string): Promise<void> {
    return loadRasterImage(src).then(() => undefined);
}

/**
 * إحماء **البايتات فقط**: ينزّل الملفّ إلى كاش الشبكة بلا فكّ وبلا إدخاله كاش الصور.
 *
 * لماذا يلزم مسار ثانٍ؟ لأن تعبئة الأيام الخمسة تمرّ بـ121 إطاراً × نوعين = ~242 صورة،
 * بينما كاش الصور المفكوكة سعته 40. فلو مرّت التعبئة من `prefetchRasterImage` (وهو ما
 * كان يحدث) لطردت **كل** ما حول موضع المشاهدة وأبقت آخر أربعين صورة محمَّلة — أي إطارات
 * اليوم الخامس التي لا ينظر إليها أحد. النتيجة التي يراها المستخدم بالضبط: يتحرّك على
 * الشريط فتختفي الألوان ثانية أو ثانيتين ثم تعود، لأن الإطار المجاور خرج من الكاش
 * ويحتاج فكّاً من جديد رغم أن بايتاته موجودة محلياً.
 *
 * الفصل يحلّ الأمرين معاً: البايتات تصل لكل الأيام (وهو الغرض الحقيقي من التعبئة)،
 * وكاش الفكّ يبقى محجوزاً لما حول الموضع الحالي وحده. وفكّ صورة من كاش الشبكة لاحقاً
 * سريع، إذ لا رحلة شبكة فيه.
 */
export function prefetchRasterBytes(src: string): Promise<void> {
    // المفاتيح الصناعية تُبنى في الذاكرة ولا تُنزَّل — أي fetch عليها يرمي خطأ نظام
    // («‏URL scheme mem is not supported») ويظهر في لوحة المشاكل كطلب CORS محجوب.
    if (src.startsWith('mem:')) return Promise.resolve();
    if (images.has(src) || pending.has(src) || missing.has(src)) return Promise.resolve();
    return fetch(src, { mode: 'cors', credentials: 'omit', cache: 'default' })
        // قراءة الجسم ضرورية: بلا استهلاكه قد لا يكتمل التخزين في كاش الشبكة.
        .then((r) => {
            if (r.status === 404 || r.status === 410) { markMissing(src); return null; }
            return r.ok ? r.arrayBuffer() : null;
        })
        // التسجيل بعد استهلاك الجسم لا قبله: قبله يعني إعلان الوصول بينما قد ينقطع
        // التدفّق في منتصفه، فيزرقّ الشريط على ساعة ستنتظر تنزيلاً كاملاً عند بلوغها.
        .then((buf) => { if (buf) markFetched(src); return undefined; })
        .catch(() => undefined);
}

/**
 * هل هناك تحميل/فكّ جارٍ لهذا الرابط الآن؟
 *
 * يُستعمل لتفادي إدراج مهمّة إحماء مكرّرة في الطابور. `loadRasterImage` يوحّد الطلبات
 * فلا ضرر وظيفي من التكرار، لكن كل مهمّة مكرّرة تحجز فتحة من فتحات التزامن الست بلا
 * عمل حقيقي — فتتأخّر إطارات أخرى ينتظرها العرض فعلاً.
 */
export function isRasterImagePending(src: string): boolean {
    return pending.has(src);
}

/**
 * ── سجلّ البايتات الواصلة ─────────────────────────────────────────────────────
 *
 * `images` كاشُ **فكّ** بسعة أربعين وطردٍ بـLRU — أي أنه يمثّل «ما هو جاهز للرفع إلى
 * ‏GPU الآن»، لا «ما وصل من الشبكة». والفرق حاسم لمؤشّر الشريط الزمني: بنوعين لكل
 * إطار لا يتّسع الكاش لأكثر من عشرين ساعة، فكان الشريط يمتلئ زرقةً ثم **ينساها** كلّما
 * دار الـLRU، ولا يمكن أن يزرقّ كاملاً مهما اكتمل التنزيل. وهو ما رآه المستخدم.
 *
 * ما يهمّ فعلاً هو زمن الانتظار: بايتات موجودة تعني فكّاً بأجزاء من الثانية على خيط
 * جانبي، بينما غيابها يعني رحلة شبكة كاملة (‏TTFB ~0.3ث + ~250 ك.ب/ث). فسجلّ البايتات
 * هو المقياس الصادق لـ«هل الانتقال إلى هذه الساعة سيكون فورياً؟».
 *
 * ولا يُطرد منه شيء: مجموعة نصوص لبضعة آلاف رابط — عشرات الكيلوبايتات، مقابل خاصيّة
 * لا تُنال بغيرها وهي أن المؤشّر **تراكمي لا ينسى**.
 */
const fetchedBytes = new Set<string>();

function markFetched(src: string): void {
    fetchedBytes.add(src);
}

/**
 * هل بايتات هذا الرابط بحوزتنا (أو ثبت أنه مفقود، فلن يُنتظر أبداً)؟
 *
 * المفقود يُحتسب «محسوماً» عمداً: 404 لا يُنتظر — يُعرف فوراً بلا رحلة شبكة (راجع
 * `missing`)، فإبقاؤه رمادياً إلى الأبد يوحي بعمل معلّق لا وجود له.
 */
export function isRasterBytesReady(src: string): boolean {
    return fetchedBytes.has(src) || images.has(src) || missing.has(src);
}

/** هل الصورة مفكوكة وجاهزة فعلياً للرفع إلى WebGL؟ */
export function isRasterImageReady(src: string): boolean {
    return images.has(src);
}
